import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Megaphone,
  Users,
  BarChart3,
  FileBarChart,
  Mail,
  Zap,
  Send,
  ShieldOff,
  PlaySquare,
  LogOut,
  User as UserIcon,
  Link2,
  Scissors,
  MessageCircle,
  ClipboardList,
  DollarSign,
} from "lucide-react";
import api from "@/lib/api";
import { BRAND_LOGO } from "@/lib/assets";
import { setAppTimezone } from "@/lib/timezone";

const NAV = [
  { to: "/admin", label: "סקירה", icon: LayoutDashboard, testId: "nav-dashboard", mobile: true },
  { to: "/admin/campaigns", label: "קמפיינים", icon: Megaphone, testId: "nav-campaigns", mobile: true },
  { to: "/admin/affiliates", label: "אפיליאייטים", icon: Link2, testId: "nav-affiliates" },
  { to: "/admin/revshare-import", label: "ייבוא Revshare", icon: DollarSign, testId: "nav-revshare-import" },
  { to: "/admin/leads", label: "לידים", icon: Users, testId: "nav-leads", mobile: true },
  { to: "/admin/questionnaire-responses", label: "שאלונים", icon: ClipboardList, testId: "nav-questionnaire" },
  { to: "/admin/emails", label: "מיילים", icon: Mail, testId: "nav-emails", mobile: true },
  { to: "/admin/drips", label: "Drip", icon: Zap, testId: "nav-drips" },
  { to: "/admin/broadcasts", label: "Broadcast", icon: Send, testId: "nav-broadcasts" },
  { to: "/admin/vod", label: "VOD", icon: PlaySquare, testId: "nav-vod" },
  { to: "/admin/short-links", label: "קיצורי לינקים", icon: Scissors, testId: "nav-short-links" },
  { to: "/admin/whatsapp", label: "WhatsApp", icon: MessageCircle, testId: "nav-whatsapp" },
  { to: "/admin/suppressions", label: "השתקות", icon: ShieldOff, testId: "nav-suppressions" },
  { to: "/admin/reports", label: "דוחות", icon: FileBarChart, testId: "nav-reports" },
  { to: "/admin/analytics", label: "אנליטיקה", icon: BarChart3, testId: "nav-analytics", mobile: true },
];

export default function AdminLayout({ children, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Skip /auth/me check if returning from OAuth callback
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        // Load the server-configured timezone once per session so all
        // admin pages format dates in the same zone (Israel by default).
        try {
          const { data: settings } = await api.get("/admin/email/settings");
          if (settings?.timezone) setAppTimezone(settings.timezone);
        } catch (_) {
          /* silent — falls back to Asia/Jerusalem default */
        }
      } catch (e) {
        navigate("/admin/login", { replace: true });
      } finally {
        setChecking(false);
      }
    })();
  }, [navigate]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed:", err);
    }
    navigate("/admin/login", { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-10 h-10 rounded-full border-4 border-brand-amber border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-stone-50 flex" dir="rtl">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-l border-stone-200 hidden md:flex flex-col">
        <div className="h-16 flex items-center px-5 border-b border-stone-200">
          <img src={BRAND_LOGO} alt="" className="h-9" />
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            const isActive =
              location.pathname === n.to ||
              (n.to !== "/admin" && location.pathname.startsWith(n.to));
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                  isActive
                    ? "bg-brand-amber text-white"
                    : "text-stone-700 hover:bg-stone-100"
                }`}
              >
                <Icon className="w-4 h-4" />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-stone-200">
          <div className="flex items-center gap-3 p-2">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
            ) : (
              <UserIcon className="w-9 h-9 p-2 rounded-full bg-stone-100" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate" data-testid="admin-user-name">{user.name}</div>
              <div className="text-xs text-stone-500 truncate">{user.email}</div>
              {user.role === "viewer" && (
                <span
                  data-testid="admin-user-role-badge"
                  className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-blue-800 bg-blue-100 border border-blue-200 rounded-full px-2 py-0.5"
                >
                  צפייה בלבד
                </span>
              )}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-stone-600 hover:bg-stone-100"
          >
            <LogOut className="w-4 h-4" />
            יציאה
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-stone-200 h-14 flex items-center justify-between px-4">
        <img src={BRAND_LOGO} alt="" className="h-7" />
        <button onClick={logout} className="text-sm text-stone-600">יציאה</button>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 md:p-8 p-4 pt-16 md:pt-8">
        {title && (
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-[800]" data-testid="admin-page-title">{title}</h1>
          </div>
        )}
        {children}

        {/* Mobile nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-stone-200 flex">
          {NAV.filter((n) => n.mobile).map((n) => {
            const Icon = n.icon;
            const isActive =
              location.pathname === n.to ||
              (n.to !== "/admin" && location.pathname.startsWith(n.to));
            return (
              <Link
                key={n.to}
                to={n.to}
                className={`flex-1 flex flex-col items-center py-2 text-xs ${
                  isActive ? "text-brand-amber" : "text-stone-500"
                }`}
              >
                <Icon className="w-5 h-5" />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="md:hidden h-16" />
      </main>
    </div>
  );
}
